import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from "react";
import Archives from "../Images/archive_icon.png";
import axios from "axios";
import "../css/ArchiveSidebar.css";

// API Server URL (use the same as in main file)
const API_SERVER_URL = "https://www.enlive.inc";

export interface ArchiveSidebarRef {
  fetchArchives: () => void;
}

interface ArchiveSidebarProps {
  toolId: number;
  openArchives: () => void;
  loadArchive: (conversationId: number) => void;
}

const ArchiveSidebar = forwardRef<ArchiveSidebarRef, ArchiveSidebarProps>((
  { toolId, openArchives, loadArchive },
  ref
) => {
  const [allConversations, setAllConversations] = useState<Array<any>>([]);
  const [visibleCount, setVisibleCount] = useState(10); // start with 10

  const [isExpanded, setIsExpanded] = useState(false);
  // State for open/close and pinned (clicked)
  const [isOpen, setIsOpen] = useState(false);
  const [isClicked, setIsClicked] = useState(false);
  const [archivedConversations, setArchivedConversations] = useState<
    Array<any>
  >([]);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);

  // Fetch archives when the sidebar opens
  useEffect(() => {
    if (isOpen) {
      fetchArchives();
    }
  }, [isOpen, visibleCount]);

  // Now, it fetches archives when sidebar opens on hover too

  // Handle outside click to close the sidebar
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        // If pinned, unpin when user clicks outside
        if (isClicked) {
          setIsClicked(false);
        }
        setIsOpen(false);
      }
    }

    if (isClicked) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isClicked]);

  // Keep sidebar open if hovered over
  // If user hasn't clicked (not pinned), leaving the sidebar closes it
  function handleSidebarMouseEnter() {
    setIsOpen(true);
  }

  function handleSidebarMouseLeave() {
    if (!isClicked) {
      setIsOpen(false);
    }
  }

  // Fetch archives
  async function fetchArchives() {
    const auth0Sub = getCookieValue("auth0_sub") || "unknown_sub";
    try {
      const resp = await axios.get(`${API_SERVER_URL}/list-conversations`, {
        params: { sub_pk: auth0Sub, tool_id: toolId },
        withCredentials: true,
      });
      setAllConversations(resp.data);
      setArchivedConversations(resp.data.slice(0, visibleCount));
    } catch (err) {
      console.error("Failed to fetch archives:", err);
    }
  }

  // Expose fetchArchives to parent via ref
  useImperativeHandle(ref, () => ({
    fetchArchives,
  }));

  // Utility to format date
  function formatArchiveDate(dateStr: string | number | Date) {
    const date = new Date(dateStr);
    return (
      date.toLocaleDateString() +
      " " +
      date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    );
  }

  // Toggle pinned state
  function handleButtonClick() {
    setIsClicked(!isClicked);
    setIsOpen(!isClicked);
  }

  // Function to generate display label from first user query
  function getConversationLabel(conv: any): string {
    // If first_user_query exists and is not empty, truncate to first 5 words
    if (conv.first_user_query && conv.first_user_query.trim().length > 0) {
      const words = conv.first_user_query.trim().split(/\s+/);
      if (words.length <= 5) {
        // If 5 words or less, show all words
        return conv.first_user_query.trim();
      } else {
        // If more than 5 words, show first 5 with ellipsis
        return words.slice(0, 5).join(" ") + "...";
      }
    }
    
    // Fallback to conversation number if no first_user_query
    return `Conversation #${conv.ConversationID}`;
  }

  return (
    <>
      {/* Archive Button on the left */}
      <div
        ref={buttonRef}
        className="archive-button"
        onMouseEnter={() => {
          // If not pinned, open on hover
          if (!isClicked) {
            setIsOpen(true);
          }
        }}
        onClick={handleButtonClick}
      >
        <img src={Archives} alt="Archive Logo" className="archive-logo" />
      </div>

      {/* Sidebar */}
      <div
        ref={sidebarRef}
        className={`sidebar ${isOpen ? "open" : ""} ${
          isExpanded ? "expanded" : ""
        }`}
        onMouseEnter={handleSidebarMouseEnter}
        onMouseLeave={handleSidebarMouseLeave}
      >
        <div className="archive-content">
          <h3>Archives</h3>
          {archivedConversations.length === 0 ? (
            <p>No archived conversations found.</p>
          ) : (
            <ul>
              {archivedConversations.map((conv) => (
                <li
                  key={conv.ConversationID}
                  onClick={() => loadArchive(conv.ConversationID)}
                  className="archive-item"
                >
                  <strong className="archive-item-title">
                    {getConversationLabel(conv)}
                  </strong>
                  <p className="small text-muted">
                    {formatArchiveDate(conv.createdAt)}
                  </p>
                </li>
              ))}
            </ul>
          )}

          {archivedConversations.length < allConversations.length ? (
            <button
              className="load-more-button"
              onClick={() => {
                setIsExpanded(true);
                setIsOpen(true);
                setVisibleCount(allConversations.length); // Load all
                setArchivedConversations(allConversations); // Display all at once
              }}
            >
              Load All
            </button>
          ) : (
            <p className="end-of-conversations">End of conversations</p>
          )}
        </div>
      </div>
    </>
  );
});

// Helper function for cookies (reuse from your existing code)
function getCookieValue(name: string): string | null {
  const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  return match ? decodeURIComponent(match[2]) : null;
}

export default ArchiveSidebar;